import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, XCircle, Loader2, ArrowRight } from "lucide-react";

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  // Razorpay flow uses order_id (new); keep session_id as fallback
  const orderId = params.get("order_id") || params.get("session_id");
  const [status, setStatus] = useState({ state: "checking", attempts: 0 });
  const pollRef = useRef(null);

  useEffect(() => {
    if (!orderId) { navigate("/pricing", { replace: true }); return; }

    let attempts = 0;
    const max = 8;
    const poll = async () => {
      attempts++;
      try {
        const { data } = await api.get(`/billing/order/${orderId}`);
        if (data.payment_status === "paid") {
          setStatus({ state: "paid", plan_id: data.plan_id, plan_name: data.plan_name });
          return;
        }
        if (data.payment_status === "signature_failed" || data.status === "failed") {
          setStatus({ state: "error" });
          return;
        }
        if (attempts >= max) {
          setStatus({ state: "timeout" });
          return;
        }
        setStatus({ state: "checking", attempts });
        pollRef.current = setTimeout(poll, 2200);
      } catch (e) {
        setStatus({ state: "error" });
      }
    };
    poll();
    return () => { if (pollRef.current) clearTimeout(pollRef.current); };
  }, [orderId, navigate]);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white sd-grain flex items-center justify-center p-6">
      <div className="max-w-lg w-full sd-card p-10 text-center">
        {status.state === "checking" && (
          <>
            <Loader2 size={32} strokeWidth={1.25} className="animate-spin text-[#FF3B30] mx-auto" />
            <div className="sd-label mt-6">CONFIRMING PAYMENT</div>
            <div className="font-display text-3xl mt-2">Just a moment…</div>
            <div className="text-sm text-zinc-500 mt-3">Verifying with Razorpay · attempt {status.attempts + 1} of 8</div>
          </>
        )}

        {status.state === "paid" && (
          <>
            <CheckCircle2 size={40} strokeWidth={1.25} className="text-[#34C759] mx-auto" />
            <div className="sd-label mt-6 text-[#34C759]">PAYMENT CONFIRMED</div>
            <div className="font-display text-4xl mt-2">You're on {status.plan_name || status.plan_id}.</div>
            <div className="text-sm text-zinc-400 mt-4 leading-relaxed">
              Welcome to the Pro-grade tools. Your subscription is active for the next 30 days.
              Everything unlocked, no waiting.
            </div>
            <Link to="/dashboard" className="sd-btn sd-btn-primary h-12 mt-8 w-full" data-testid="billing-back-to-app-btn">
              Back to workspace <ArrowRight size={14} strokeWidth={1.5} />
            </Link>
          </>
        )}

        {(status.state === "timeout" || status.state === "error") && (
          <>
            <XCircle size={40} strokeWidth={1.25} className="text-[#FF3B30] mx-auto" />
            <div className="sd-label mt-6 text-[#FF3B30]">
              {status.state === "timeout" ? "TIMED OUT" : "PAYMENT ERROR"}
            </div>
            <div className="font-display text-3xl mt-2">Something went sideways.</div>
            <div className="text-sm text-zinc-500 mt-3">
              If you were charged, contact us with your order ID:
              <div className="font-mono text-xs mt-2 text-zinc-400 break-all">{orderId}</div>
              Otherwise, try again from pricing.
            </div>
            <div className="flex gap-3 mt-8">
              <Link to="/pricing" className="sd-btn sd-btn-primary h-12 flex-1" data-testid="billing-retry-btn">
                Back to pricing
              </Link>
              <Link to="/contact" className="sd-btn sd-btn-ghost h-12 flex-1">
                Contact us
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
